import { useState, useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Field, FieldGroup, FieldLabel, FieldSet,
} from '@/components/ui/field'
import { DatePicker } from '@/components/DatePicker'
import { LanguagePicker } from '@/components/LanguagePicker'
import { extractErr } from '@/components/ui/inline-edit'
import { blobErr } from '../lib/blobErr'
import TemplateEditor, { TemplateEditorHandle } from '../components/TemplateEditor'

type Template = {
  id: number
  category: string
  category_label: string
  source_type: string
  subsidiary: number | null
  subsidiary_code: string | null
  language_code: string
  version: number
  effective_from: string
  effective_until: string | null
  name: string
  description: string
  filename_pattern: string
  body_html: string
  body_file: string | null
  body_file_filename: string | null
  format: 'HTML' | 'DOCX_UPLOAD'
  active: boolean
  signable: boolean
}

type SchemaEntry =
  | string
  | { path: string; kind?: 'scalar' | 'loop'; label?: string; snippet?: string }

type Category = {
  category: string
  label: string
  source_type: string
  schema: SchemaEntry[]
}

export default function DocumentTemplateDetail() {
  const { id } = useParams<{ id: string }>()
  const templateId = id ? Number(id) : 0
  const qc = useQueryClient()

  const tplQ = useQuery({
    queryKey: ['document-template', templateId],
    queryFn: async () =>
      (await api.get<Template>(`/document-templates/${templateId}/`)).data,
    enabled: !!templateId,
  })

  const categoriesQ = useQuery({
    queryKey: ['document-templates', 'categories'],
    queryFn: async () =>
      (await api.get<Category[]>('/document-templates/categories/')).data,
  })

  const [form, setForm] = useState<Partial<Template>>({})
  const editorRef = useRef<TemplateEditorHandle>(null)

  // The variables sidebar inserts into whichever field was last focused — the
  // body editor (default) or the file-name input. The shadcn <Input> is a plain
  // function component (no forwardRef), so the element and the caret are taken
  // off the input's own events rather than from a ref on it.
  const [insertTarget, setInsertTarget] = useState<'body' | 'filename'>('body')
  const filenameEl = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (tplQ.data) setForm(tplQ.data)
  }, [tplQ.data])

  const saveMut = useMutation({
    mutationFn: async (payload: Partial<Template>) =>
      (await api.patch(`/document-templates/${templateId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-template', templateId] })
      qc.invalidateQueries({ queryKey: ['document-templates'] })
      toast.success('Template saved')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const uploadBodyMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('body', file)
      return (await api.post(`/document-templates/${templateId}/upload-body/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data as Template
    },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['document-template', templateId] })
      setForm(t)
      toast.success('DOCX template uploaded')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const lintMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/document-templates/${templateId}/lint/`)).data as {
        tokens: string[]
        unknown: string[]
        syntax_error?: string
      },
    onSuccess: (r) => {
      if (r.syntax_error) {
        toast.error(`Syntax error: ${r.syntax_error}`, { duration: 12000 })
        return
      }
      if (r.unknown.length === 0) {
        toast.success(`Lint clean — ${r.tokens.length} token${r.tokens.length === 1 ? '' : 's'}, all known.`)
      } else {
        toast.error(
          `${r.unknown.length} unknown top-level variable${r.unknown.length === 1 ? '' : 's'}: ${r.unknown.slice(0, 5).join(', ')}${r.unknown.length > 5 ? '…' : ''}`,
          { duration: 12000 },
        )
      }
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (tplQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (!tplQ.data) {
    return (
      <AdminLayout>
        <p className="text-foreground mb-4">Template not found.</p>
        <Link to="/admin/document-templates" className="text-blue-600 hover:underline">← Back</Link>
      </AdminLayout>
    )
  }

  const tpl = tplQ.data
  const categoryEntry = (categoriesQ.data || []).find((c) => c.category === tpl.category)
  const schema = categoryEntry?.schema || []

  const save = () => {
    saveMut.mutate({
      name: form.name,
      description: form.description,
      filename_pattern: form.filename_pattern,
      language_code: (form.language_code || '').toLowerCase(),
      version: form.version,
      effective_from: form.effective_from,
      effective_until: form.effective_until || null,
      format: form.format,
      body_html: form.body_html,
      active: form.active,
      signable: form.signable,
    })
  }

  // Route a clicked token to the body editor or into the file-name input at
  // the caret (the click itself blurred the input, so focus is restored after).
  const insertToken = (text: string) => {
    if (insertTarget !== 'filename') {
      editorRef.current?.insertText(text)
      return
    }
    const el = filenameEl.current
    const current = form.filename_pattern || ''
    const start = el?.selectionStart ?? current.length
    const end = el?.selectionEnd ?? current.length
    setForm((f) => ({
      ...f,
      filename_pattern: current.slice(0, start) + text + current.slice(end),
    }))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + text.length, start + text.length)
    })
  }

  return (
    <AdminLayout>
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/document-templates" className="text-muted-foreground hover:text-foreground hover:underline">
            ← Document templates
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {tpl.category_label}
              <span className="ml-3 text-base text-muted-foreground font-normal">
                {tpl.subsidiary_code || 'Global'} · {tpl.language_code || 'any lang'} · v{tpl.version}
              </span>
            </h2>
            <div className="text-xs text-muted-foreground font-mono">
              {tpl.category} · binds to {tpl.source_type}
            </div>
          </div>
          <Button onClick={save} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_360px] gap-6">
        {/* Left: editor */}
        <div className="space-y-4">
          <section className="bg-card rounded-lg shadow p-6">
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel>Name</FieldLabel>
                  <Input
                    value={form.name || ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Standard offer letter — Germany"
                  />
                </Field>
                <Field>
                  <FieldLabel>Description</FieldLabel>
                  <Textarea
                    rows={2}
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </Field>
                <Field>
                  <FieldLabel>File name</FieldLabel>
                  <Input
                    value={form.filename_pattern || ''}
                    onChange={(e) => setForm({ ...form, filename_pattern: e.target.value })}
                    onFocus={(e) => {
                      filenameEl.current = e.currentTarget
                      setInsertTarget('filename')
                    }}
                    placeholder={'PO {{ po.number }}'}
                  />
                  <span className="text-xs text-muted-foreground">
                    Names the generated file. Takes the same tokens as the body — e.g.{' '}
                    <code>{'ICA {{ contract.number }} {{ today }}'}</code>. The extension is added
                    automatically. Leave blank for a default name.
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Generated file:{' '}
                    <span className="font-mono text-foreground">
                      {filenamePreview(form.filename_pattern || '', tpl.category)}
                    </span>
                    {(form.filename_pattern || '').trim()
                      ? ' — tokens resolve when the document is generated'
                      : ' — the default'}
                  </span>
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field>
                    <FieldLabel>Language</FieldLabel>
                    <LanguagePicker
                      value={form.language_code || ''}
                      onChange={(code) => setForm({ ...form, language_code: code })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Version</FieldLabel>
                    <Input
                      type="number" min={1}
                      value={form.version ?? 1}
                      onChange={(e) => setForm({ ...form, version: Number(e.target.value || 1) })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Active</FieldLabel>
                    <label className="flex items-center h-10 space-x-2">
                      <input
                        type="checkbox"
                        checked={!!form.active}
                        onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      />
                      <span className="text-sm text-muted-foreground">Live</span>
                    </label>
                  </Field>
                </div>
                <Field>
                  <FieldLabel>Signing workflow</FieldLabel>
                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.signable !== false}
                      onChange={(e) => setForm({ ...form, signable: e.target.checked })}
                    />
                    <span className="text-sm">
                      <span className="text-foreground">Signable</span>
                      <span className="block text-xs text-muted-foreground">
                        When on, generated docs pass through draft → awaiting signature → signed. Turn off for informational documents that don't get signed (e.g. profile sheets).
                      </span>
                    </span>
                  </label>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field>
                    <FieldLabel>Effective from</FieldLabel>
                    <DatePicker
                      value={form.effective_from || ''}
                      onChange={(v) => setForm({ ...form, effective_from: v })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Effective until</FieldLabel>
                    <DatePicker
                      value={form.effective_until || ''}
                      onChange={(v) => setForm({ ...form, effective_until: v || null })}
                    />
                  </Field>
                </div>
              </FieldGroup>
            </FieldSet>
          </section>

          <section className="bg-card rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Body
              </h3>
              {/* Format toggle */}
              <div className="inline-flex items-center rounded-md border bg-card p-1 text-xs">
                {([
                  { key: 'HTML',        label: 'Online editor' },
                  { key: 'DOCX_UPLOAD', label: 'DOCX upload' },
                ] as { key: Template['format']; label: string }[]).map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, format: o.key }))}
                    className={`px-3 py-1 rounded ${
                      form.format === o.key
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {form.format === 'DOCX_UPLOAD' ? (
              <DocxUploadPanel
                form={form}
                templateId={templateId}
                uploading={uploadBodyMut.isPending}
                linting={lintMut.isPending}
                onUpload={(file) => uploadBodyMut.mutate(file)}
                onLint={() => lintMut.mutate()}
              />
            ) : (
              <div onFocus={() => setInsertTarget('body')}>
                <TemplateEditor
                  ref={editorRef}
                  value={form.body_html || ''}
                  onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
                />
              </div>
            )}
          </section>
        </div>

        {/* Right: tabbed Variables / Preview panel */}
        <aside>
          <SidePanel
            schema={schema}
            category={tpl.category}
            bodyHtml={form.body_html || ''}
            templateId={tpl.id}
            format={form.format || 'HTML'}
            insertTarget={insertTarget}
            onInsert={insertToken}
          />
        </aside>
      </div>
    </AdminLayout>
  )
}


// ─────────── File name preview ───────────


/** Local ISO date — matches the renderer's ``date.today().isoformat()``. */
function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** What the generated file will be called, as far as the browser can know.
 *  Sample token values are not available client-side (the preview endpoint
 *  hands back rendered HTML, not the provider context), so a pattern is shown
 *  as typed — its tokens resolve server-side at generation time. A blank
 *  pattern mirrors the renderer's default ``<category>-<source id>-<date>``. */
function filenamePreview(pattern: string, category: string): string {
  const base = pattern.trim() || `${category.replace(/\./g, '_')}-<source id>-${todayIso()}`
  // The renderer does not double an extension the author already typed.
  return /\.(pdf|docx)$/i.test(base) ? base : `${base}.pdf`
}


// ─────────── Side panel: Variables / Preview tabs ───────────


function SidePanel({
  schema, category, bodyHtml, templateId, format, insertTarget, onInsert,
}: {
  schema: SchemaEntry[]
  category: string
  bodyHtml: string
  templateId: number
  format: 'HTML' | 'DOCX_UPLOAD'
  insertTarget: 'body' | 'filename'
  onInsert: (text: string) => void
}) {
  const [tab, setTab] = useState<'variables' | 'preview'>('variables')

  return (
    <div className="bg-card rounded-lg shadow sticky top-4 overflow-hidden">
      <div className="flex border-b">
        <button
          type="button"
          onClick={() => setTab('variables')}
          className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
            tab === 'variables' ? 'bg-muted/40 text-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Variables
        </button>
        <button
          type="button"
          onClick={() => setTab('preview')}
          className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide ${
            tab === 'preview' ? 'bg-muted/40 text-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Preview
        </button>
      </div>

      {tab === 'variables' && (
        <div className="p-4">
          <p className="text-xs text-muted-foreground mb-3">
            Click a variable to insert it into the{' '}
            <span className="font-medium text-foreground">
              {insertTarget === 'filename' ? 'file name' : 'body'}
            </span>{' '}
            — whichever you last clicked into. Use double-brace syntax inside the body.
          </p>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {schema.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No data provider schema available.</p>
              ) : (
                schema.map((v, i) => {
                  const entry = typeof v === 'string'
                    ? { path: v, kind: 'scalar' as const, label: v, snippet: undefined }
                    : { kind: 'scalar' as const, ...v }
                  const isLoop = entry.kind === 'loop'
                  const toInsert = entry.snippet || `{{ ${entry.path} }}`
                  return (
                    <button
                      key={`${entry.path}-${i}`}
                      type="button"
                      onClick={() => onInsert(toInsert)}
                      className={`block w-full text-left px-2 py-1 rounded text-xs hover:bg-muted/50 ${
                        isLoop ? 'font-medium text-blue-700' : 'font-mono text-foreground'
                      }`}
                      title={isLoop ? 'Inserts a {% for %} loop block' : `Inserts {{ ${entry.path} }}`}
                    >
                      {isLoop ? (
                        <>
                          <span className="text-[10px] uppercase tracking-wide mr-1 bg-blue-100 px-1 rounded">loop</span>
                          {entry.label || entry.path}
                        </>
                      ) : (
                        `{{ ${entry.path} }}`
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )}

        {tab === 'preview' && (
          <PreviewPane
            category={category}
            bodyHtml={bodyHtml}
            templateId={templateId}
            format={format}
          />
        )}
    </div>
  )
}


function PreviewPane({
  category, bodyHtml, templateId, format,
}: {
  category: string
  bodyHtml: string
  templateId: number
  format: 'HTML' | 'DOCX_UPLOAD'
}) {
  const [html, setHtml] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(false)

  // Live debounced preview for HTML templates. DOCX previews are slow
  // (~3-8s LibreOffice round-trip) so we trigger them on a button click.
  const debounced = useDebouncedValue(bodyHtml, 500)

  const fetchPreview = (manual = false) => {
    setLoading(true)
    setError('')
    const payload: any = { category }
    if (format === 'DOCX_UPLOAD') {
      payload.template_id = templateId
    } else {
      payload.body_html = manual ? bodyHtml : debounced
    }
    api.post('/document-templates/preview/', payload)
      .then((r) => setHtml(r.data.html || ''))
      .catch((e) => setError(extractErr(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!category) return
    if (format === 'HTML') {
      fetchPreview()
    }
    // For DOCX_UPLOAD we wait for the user to click the Refresh button.
    // (Initial fetch on mount would block the UI for several seconds.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, format, debounced])

  return (
    <div className="p-2">
      <div className="text-xs text-muted-foreground px-2 py-1 flex items-center justify-between">
        <span>
          Rendered with sample data
          {format === 'DOCX_UPLOAD' && <> · <span className="italic">DOCX preview is slow (~5s)</span></>}
        </span>
        {format === 'DOCX_UPLOAD' ? (
          <button
            type="button"
            onClick={() => fetchPreview(true)}
            disabled={loading}
            className="text-blue-600 hover:underline disabled:opacity-50"
          >
            {loading ? 'Rendering…' : 'Refresh preview'}
          </button>
        ) : (
          loading && <span className="italic">refreshing…</span>
        )}
      </div>
      {error ? (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      ) : (
        <iframe
          title="Template preview"
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:'Helvetica Neue',Arial,sans-serif;color:#222;font-size:11pt;line-height:1.45;padding:14px;} table{border-collapse:collapse;width:100%;} td,th{border:1px solid #ddd;padding:4px 6px;} th{background:#f4f6fa;}</style></head><body>${html || (format === 'DOCX_UPLOAD' ? '<p style="color:#888;font-style:italic;">Click "Refresh preview" to render the DOCX template.</p>' : '')}</body></html>`}
          className="w-full bg-white border rounded"
          style={{ height: '70vh' }}
          sandbox=""
        />
      )}
    </div>
  )
}


// ─────────── DOCX upload panel ───────────


function DocxUploadPanel({
  form, templateId, uploading, linting, onUpload, onLint,
}: {
  form: Partial<Template>
  templateId: number
  uploading: boolean
  linting: boolean
  onUpload: (file: File) => void
  onLint: () => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)

  const hasFile = !!form.body_file
  const filename = form.body_file_filename || ''

  const downloadStarter = () => {
    api.get(`/document-templates/${templateId}/starter/`, { responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })
        const cd = res.headers['content-disposition'] || ''
        const m = /filename="([^"]+)"/.exec(cd)
        const fname = m ? m[1] : 'starter.docx'
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fname
        link.click()
        window.URL.revokeObjectURL(link.href)
      })
      .catch(async (e) => toast.error(await blobErr(e)))
  }

  return (
    <div className="border rounded-md p-4 bg-muted/10 space-y-3">
      <input
        ref={fileRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onUpload(f)
          e.target.value = ''
        }}
      />

      {hasFile ? (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="font-medium text-foreground">{filename || 'template.docx'}</div>
            <div className="text-xs text-muted-foreground">
              Uploaded DOCX template. Edit it offline in Word or LibreOffice, then replace it here.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={downloadStarter}>Download starter</Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Replace…'}
            </Button>
            <Button variant="outline" onClick={onLint} disabled={linting}>
              {linting ? 'Linting…' : 'Run lint'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-3">
            No DOCX file uploaded yet. Two ways to start:
          </p>
          <div className="flex items-center justify-center gap-2 mb-3">
            <Button variant="outline" onClick={downloadStarter}>
              Download starter
            </Button>
            <span className="text-xs text-muted-foreground">— edit in Word, then upload</span>
          </div>
          <div className="text-xs text-muted-foreground mb-3">— or —</div>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload your own .docx'}
          </Button>
        </div>
      )}

      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          <span className="font-semibold">Syntax — Jinja2</span> (the same syntax used by the online
          editor; templates are portable between formats). Common patterns:
        </p>
        <ul className="ml-4 list-disc space-y-0.5">
          <li><code>{'{{ person.first_name }}'}</code> — insert a value</li>
          <li><code>{'{% for s in skills %}{{ s.name }}{% endfor %}'}</code> — loop</li>
          <li><code>{'{{ industries|join(", ") }}'}</code> — filter args use parens</li>
          <li><code>{'{% if loop.last %}'}</code> — Jinja's <code>loop</code> (not <code>forloop</code>)</li>
          <li><code>{'{{ d.strftime("%Y-%m-%d") }}'}</code> — date formatting via Python methods</li>
        </ul>
        <p>
          <span className="font-semibold">Lint</span> — extracts every token in the uploaded DOCX and
          flags unknowns vs the data provider's schema. Run after every upload — silent unknown tokens
          render as empty strings, easy to miss.
        </p>
      </div>
    </div>
  )
}


function useDebouncedValue<T>(value: T, delay: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}
